/**
 * Fund Compare Hook
 *
 * Fetches comparison data for multiple funds using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, queryOptions } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";
import type { Fund, FundIsin, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Response data structure from the compare endpoint
 */
export interface FundCompareData {
  comparison: Fund[];
  /** Rank history keyed by fund ISIN */
  rankHistory: Record<string, RankHistoryEntry[]>;
  requestedCount: number;
  foundCount: number;
}

/**
 * Parameters for the fund compare query
 */
export interface FundCompareParams {
  /** Array of fund ISINs to compare (minimum 2, maximum 10) */
  isins: FundIsin[] | string[];
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for fund compare queries
 */
export const fundCompareKeys = {
  all: ["funds", "compare"] as const,
  compare: (isins: string[]) => [...fundCompareKeys.all, isins.sort().join(",")] as const,
};

// ============================================================================
// Query Options Factory
// ============================================================================

/**
 * Creates query options for fund comparison
 * Can be used with useQuery, prefetching, or useSuspenseQuery
 *
 * @param params - The query parameters including ISINs to compare
 * @returns TanStack Query options object
 */
export function fundCompareQueryOptions({ isins }: FundCompareParams) {
  const normalizedIsins = isins.map((isin) => String(isin));

  return queryOptions({
    queryKey: fundCompareKeys.compare(normalizedIsins),
    queryFn: async (): Promise<FundCompareData> => {
      // Use Hono RPC client for type-safe POST request
      const response = await api.v1.funds.compare.$post({
        json: { isins: normalizedIsins },
      });

      return unwrapResponse<FundCompareData>(response);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    // Only run if we have at least 2 ISINs
    enabled: normalizedIsins.length >= 2,
    // Retry for transient failures, but not for 4xx errors
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching fund comparison data
 *
 * Features:
 * - Only fetches when 2+ ISINs are provided
 * - Proper caching based on ISINs
 * - Loading, error, and data states
 * - Returns Fund[] with all comparison metrics
 *
 * @param isins - Array of fund ISINs to compare
 * @returns TanStack Query result with comparison data
 *
 * @example
 * ```tsx
 * function CompareScreen() {
 *   const isins = ['INF209K01YN0', 'INF090I01239'];
 *
 *   const {
 *     data,
 *     isLoading,
 *     isError,
 *     error,
 *     refetch
 *   } = useFundCompare(isins);
 *
 *   if (isLoading) return <CompareSkeleton />;
 *   if (isError) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <CompareTable funds={data.comparison} />
 *   );
 * }
 * ```
 */
export function useFundCompare(isins: FundIsin[] | string[]) {
  return useQuery(fundCompareQueryOptions({ isins }));
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isFundCompareApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get user-friendly error message for fund compare errors
 */
export function getFundCompareErrorMessage(error: unknown): string {
  if (isFundCompareApiError(error)) {
    if (error.status === 400) {
      return "Please select at least 2 funds to compare (maximum 10).";
    }
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred while loading fund comparison.";
}
